import { useTranslation } from "next-i18next";

import { ProductBySlugNonNullable } from "@/common/contracts/pdp.contracts";
import { cn } from "@/common/utils/css.utils";
import ProductUtils from "@/components/ProductDetail/utils/ProductUtils";
import { ProductBadge } from "@/components/product/ProductBadge";
import { usePdpReservation } from "@/features/pro-buyer-reservations/hooks/usePdpReservation";
import useMerchant from "@/hooks/useMerchant";

type ProductDetailsLabelProps = {
  product: ProductBySlugNonNullable;
  className?: string;
};

/** The badge over the PDP gallery. Same look as the card's; the PDP just knows a few more states. */
const ProductDetailsLabel = ({
  product,
  className,
}: ProductDetailsLabelProps) => {
  const { t } = useTranslation();
  const { id: userId } = useMerchant();

  const reservation = usePdpReservation(product.id, !!product.isOwner);

  const badgeClassName = cn("pointer-events-none z-10", className);

  if (ProductUtils.isSold(product)) {
    return (
      <ProductBadge tone="sold" className={badgeClassName}>
        {t("common:auction-state-already-sold")}
      </ProductBadge>
    );
  }

  if (product.merchant.vacationModeEnabledAt) {
    return (
      <ProductBadge tone="sold" className={badgeClassName}>
        {t("pdp:seller-on-vacation")}
      </ProductBadge>
    );
  }

  // WHO-5947: grey "Reserved" for everyone but the reserving Pro Buyer.
  if (reservation.mode === "others") {
    return (
      <ProductBadge tone="reserved" className={badgeClassName}>
        {t("common:reservation-reserved-label", "Reserved")}
      </ProductBadge>
    );
  }

  const { isLoweredInPrice, discountPercentage } =
    ProductUtils.getPriceInfo(product);

  if (isLoweredInPrice) {
    return (
      <ProductBadge
        tone="discount"
        /* Larger than the card's — it's the headline fact on the PDP gallery. */
        className={cn(badgeClassName, "px-3 py-1 text-lg")}
      >
        -{discountPercentage}%
      </ProductBadge>
    );
  }

  if (ProductUtils.isProductOwner(product, { id: userId })) {
    return (
      <ProductBadge tone="owner" className={badgeClassName}>
        {t("pdp:pdp-label-this-is-yours")}
      </ProductBadge>
    );
  }

  return null;
};

export default ProductDetailsLabel;
